// RequireAdmin — fail-closed auth gate with retryable connection failures.
//
// The server is the source of truth: studio-admin-check (verifyAdmin) gates
// the whole UI, and every write re-checks admin server-side.
//
// The verdict is cached per user id. Supabase fires SIGNED_IN on every tab
// refocus and TOKEN_REFRESHED on every refresh; re-checking on each of those
// flipped the gate to 'loading', unmounted the router and cost ~3,000
// admin-check calls a day. Now:
//   - the check runs once per user id (and again only after Retry);
//   - SIGNED_OUT, or a session for a different user, clears the cache;
//   - once 'ok', the gate never drops back to 'loading' for the same user.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { clearUser, identifyUser } from '@/lib/observability';
import { LoginForm } from './LoginForm';
import { AccessDenied } from './AccessDenied';
import { ShellLoading } from '../feedback/ShellLoading';

type AuthState = 'loading' | 'unauth' | 'denied' | 'error' | 'ok';
type Verdict = 'ok' | 'denied';

interface Props {
  children: ReactNode;
}

export function RequireAdmin({ children }: Props): JSX.Element {
  const [state, setState] = useState<AuthState>('loading');
  const [attempt, setAttempt] = useState(0);
  // userId → settled verdict. Survives Retry (a retry only follows 'error',
  // which is never cached) and auth events for the same user.
  const verdicts = useRef(new Map<string, Verdict>());
  const currentUser = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    let sequence = 0;

    const evaluate = async (session: Session | null): Promise<void> => {
      if (!alive) return;
      const request = ++sequence;
      if (!session) {
        // Sign-out / no session: drop the observability identity before the
        // state flips so nothing after this is attributed to the old operator.
        verdicts.current.clear();
        currentUser.current = null;
        clearUser();
        setState('unauth');
        return;
      }
      const userId = session.user.id;
      if (currentUser.current !== userId) {
        verdicts.current.clear();
        currentUser.current = userId;
        // Identify by user id only (never email), even if the check fails.
        identifyUser(userId);
      }
      const cached = verdicts.current.get(userId);
      if (cached) {
        setState(cached);
        return;
      }
      setState((prev) => (prev === 'ok' ? prev : 'loading'));
      try {
        const { error } = await supabase.functions.invoke('studio-admin-check');
        if (!alive || request !== sequence) return;
        const status = (error as { context?: { status?: number } } | null)?.context?.status;
        if (!error) {
          verdicts.current.set(userId, 'ok');
          setState('ok');
        } else if (status === 401 || status === 403) {
          verdicts.current.set(userId, 'denied');
          setState('denied');
        } else {
          setState('error');
        }
      } catch {
        if (alive && request === sequence) setState('error');
      }
    };

    // A delayed initial read must not replace a more recent auth event.
    const initialSequence = sequence;
    void supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!alive || sequence !== initialSequence) return;
        if (error) setState('error');
        else void evaluate(data.session);
      })
      .catch(() => {
        if (alive && sequence === initialSequence) setState('error');
      });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      void evaluate(session);
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [attempt]);

  if (state === 'loading') return <ShellLoading />;
  if (state === 'unauth') return <LoginForm />;
  if (state === 'denied') return <AccessDenied />;
  if (state === 'error') {
    return (
      <main className="grid min-h-screen place-items-center bg-surface-0 p-6">
        <div role="alert" className="max-w-md space-y-3 text-center">
          <h1 className="text-lg font-semibold text-fg-default">Couldn’t verify access</h1>
          <p className="text-sm text-fg-muted">
            Studio couldn’t check your session. Check your connection and try again.
          </p>
          <button
            type="button"
            onClick={() => {
              setState('loading');
              setAttempt((value) => value + 1);
            }}
            className="rounded bg-accent-9 px-4 py-2 text-sm font-medium text-fg-on-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            Retry
          </button>
        </div>
      </main>
    );
  }
  return <>{children}</>;
}
